import React from "react"
import { useTranslation } from "react-i18next"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import { PkmWithCustom } from "../../../../../types"
import {
  Berries,
  CraftableItems,
  FishingRods,
  Item,
  ItemComponents,
  MemoryDiscs,
  Mulches,
  ShinyItems,
  TMs,
  Tools,
  Wands,
  WeatherRocks
} from "../../../../../types/enum/Item"
import { ItemDetailTooltip } from "../../../game/components/item-detail"
import { cc } from "../../utils/jsx"

export default function ItemPicker(props: {
  selected?: PkmWithCustom | Item
  selectEntity?: (selection: PkmWithCustom | Item) => void
  onItemDragStateChange?: (dragging: boolean) => void
  showUnholdableItems?: boolean
}) {
  const { t } = useTranslation()
  const translate = (key: string) => t(key as never) as string
  function handleOnDragStart(e: React.DragEvent, item: Item) {
    e.stopPropagation()
    e.dataTransfer.effectAllowed = "copy"
    e.dataTransfer.setData("application/x-pac-item", item)
    e.dataTransfer.setData("text/plain", `item,${item}`)
    const preview = document.createElement("canvas")
    preview.width = 1
    preview.height = 1
    e.dataTransfer.setDragImage(preview, 0, 0)

    props.onItemDragStateChange?.(true)
  }

  const unholdableTabs = props.showUnholdableItems
    ? [
        { label: "Weather", key: "weather", items: WeatherRocks },
        { label: "Wands", key: "wands", items: Wands },
        { label: "Rods", key: "rods", items: FishingRods }
      ]
    : []

  const tabs = [
    { label: translate("components"), key: "components", items: ItemComponents },
    { label: translate("craftable_items"), key: "craftable", items: CraftableItems },
    {
      label: translate("food"),
      key: "food",
      items: [
        ...Berries,
        Item.TART_APPLE,
        Item.SWEET_APPLE,
        Item.SIRUPY_APPLE,
        Item.CHEF_HAT,
        ...(props.showUnholdableItems ? [Item.SMOKED_FILET] : [])
      ]
    },

    { label: translate("tools"), key: "tools", items: Tools },
    {
      label: translate("tm_short"),
      key: "tm",
      items: TMs
    },
    {
      label: "Mulch",
      key: "mulch",
      items: Mulches
    },
    ...unholdableTabs,
    {
      label: translate("shiny_items"),
      key: "shiny_items",
      items: ShinyItems
    },
    {
      label: translate("special_items"),
      key: "special_items",
      items: [
        Item.FIRE_SHARD,
        Item.CELL_BATTERY,
        Item.RUSTED_SWORD,
        Item.TEAL_MASK,
        Item.WELLSPRING_MASK,
        Item.CORNERSTONE_MASK,
        Item.HEARTHFLAME_MASK,
        ...MemoryDiscs
      ]
    }
  ]

  return (
    <Tabs className="my-box" id="item-picker">
      <TabList>
        {tabs.map((t) => (
          <Tab key={t.key}>{t.label}</Tab>
        ))}
      </TabList>
      {tabs.map((t) => (
        <TabPanel key={t.key}>
          {t.items.map((item) => (
            <img
              key={item}
              src={"assets/item/" + Item[item] + ".png"}
              className={cc("item", {
                selected: item === props.selected
              })}
              data-tooltip-id="item-detail-tooltip"
              data-tooltip-content={item}
              onClick={() => props.selectEntity?.(item)}
              draggable
              onDragStart={(e) => handleOnDragStart(e, item)}
              onDragEnd={() => props.onItemDragStateChange?.(false)}
            />
          ))}
        </TabPanel>
      ))}
      <ItemDetailTooltip />
    </Tabs>
  )
}
